import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ReaderMap } from "@/pages/admin/ReaderMap";

const PERIODS = [["today", "Today"], ["week", "Last 7 days"], ["month", "Last 30 days"], ["all_time", "All time"]];

const StatCard = ({ id, label, data }) => (
  <div className="bg-white border border-[var(--rule)] p-6" data-testid={`visits-card-${id}`}>
    <p className="meta">{label}</p>
    <p className="mt-3 font-display text-5xl font-bold tabular-nums" data-testid={`visits-views-${id}`}>{data.views.toLocaleString("en-GB")}</p>
    <p className="mt-1 font-ui text-xs text-[var(--ink-3)]">page views</p>
    <p className="mt-4 font-ui text-sm text-[var(--ink-2)]" data-testid={`visits-visitors-${id}`}>
      <span className="font-semibold text-[var(--ink)] tabular-nums">{data.visitors.toLocaleString("en-GB")}</span> visitors
    </p>
  </div>
);

const DailyChart = ({ daily }) => {
  const max = Math.max(1, ...daily.map((d) => d.views));
  return (
    <div className="bg-white border border-[var(--rule)] p-6" data-testid="visits-daily-chart">
      <p className="meta">Daily page views — last 14 days</p>
      <div className="mt-6 flex items-end gap-2 h-40">
        {daily.map((d) => (
          <div key={d.day} className="flex-1 flex flex-col items-center justify-end h-full group" title={`${d.day}: ${d.views} views, ${d.visitors} visitors`}>
            <span className="font-ui text-[10px] text-[var(--ink-3)] opacity-0 group-hover:opacity-100 transition-opacity">{d.views}</span>
            <div className="w-full bg-[var(--ink)] group-hover:bg-[var(--accent)] transition-colors" style={{ height: `${(d.views / max) * 100}%`, minHeight: d.views ? 2 : 0 }} />
          </div>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        {daily.map((d) => <span key={d.day} className="flex-1 text-center font-ui text-[10px] text-[var(--ink-3)]">{Number(d.day.slice(8))}</span>)}
      </div>
    </div>
  );
};

const TopPages = ({ pages }) => (
  <div className="bg-white border border-[var(--rule)]" data-testid="visits-top-pages">
    <p className="meta p-6 pb-3">Most visited — last 30 days</p>
    {pages.length === 0 && <p className="px-6 pb-6 italic text-[var(--ink-3)]">No visits yet.</p>}
    {pages.map((p) => (
      <div key={p.path} className="flex items-center gap-4 px-6 py-3 border-t border-[var(--rule)]">
        <Link to={p.path} target="_blank" className="flex-1 truncate font-ui text-sm link-quiet">{p.title || p.path}</Link>
        <span className="font-ui text-sm font-semibold tabular-nums">{p.views}</span>
      </div>
    ))}
  </div>
);

const Sources = ({ sources }) => {
  const total = sources.reduce((n, s) => n + s.visits, 0) || 1;
  return (
    <div className="bg-white border border-[var(--rule)] p-6" data-testid="visits-sources">
      <p className="meta">Where visitors came from — last 30 days</p>
      {sources.length === 0 && <p className="mt-4 italic text-[var(--ink-3)]">No visits yet.</p>}
      <div className="mt-5 grid gap-3">
        {sources.map((s) => (
          <div key={s.source} data-testid={`visits-source-${s.source.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="flex justify-between font-ui text-sm"><span>{s.source}</span><span className="font-semibold tabular-nums">{s.visits}</span></div>
            <div className="mt-1 h-1.5 bg-[#EFEFEC]"><div className="h-full bg-[var(--ink)]" style={{ width: `${(s.visits / total) * 100}%` }} /></div>
          </div>
        ))}
      </div>
      <p className="mt-5 font-ui text-xs text-[var(--ink-3)]">Counted once per visit, on the first page. “Direct” means typed in, bookmarked, or from an app that hides its source.</p>
    </div>
  );
};

export default function AdminVisits() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <p className="meta">Loading</p>;
  return (
    <div data-testid="admin-visits-page">
      <p className="meta">Private</p>
      <h1 className="font-display text-4xl font-bold mt-1">Visits</h1>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {PERIODS.map(([id, label]) => <StatCard key={id} id={id} label={label} data={stats[id]} />)}
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-[3fr_2fr]">
        <DailyChart daily={stats.daily} />
        <TopPages pages={stats.top_pages} />
      </div>
      <div className="mt-6"><ReaderMap data={stats.countries || []} /></div>
      <div className="mt-6 lg:w-[40%]"><Sources sources={stats.sources} /></div>
      <p className="mt-6 font-ui text-xs text-[var(--ink-3)]">Visitors are counted once per day per device. Only you can see this page.</p>
    </div>
  );
}
